import { StatusPill } from 'component-library';
import { GetLicenseColor, GetStatusColor, GetStatusText } from '../../helpers/Custom';
import { SystemsListProps } from '../../helpers/SystemListSetup';

interface Props {
  systems: SystemsListProps[];
  searchValue: string;
}

function SystemsMiniDashboard({ systems, searchValue }: Props) {
  const totalSystems = systems.length;
  const systemsWithBoatModel = systems.filter((system) => Boolean(system.boatModel?.trim())).length;
  const systemsWithHullNumber = systems.filter((system) => Boolean(system.hullNumber?.trim())).length;
  const activeSystems = systems.filter((system) => system.status === 'Active').length;

  const licenseCounts: Record<string, { count: number; color: string }> = {};
  const statusCounts: Record<string, { count: number; color: string }> = {};

  systems.forEach((system) => {
    const rawLicenseValue = system.software?.trim() || '';
    const licenseKey = rawLicenseValue || 'No license';
    const licenseColor = (GetLicenseColor(rawLicenseValue) as string) || 'Light Gray';
    const statusKey = GetStatusText(system.status) || system.status || 'Unknown';
    const statusColor = (GetStatusColor(system.status) as string) || 'Light Gray';

    if (!licenseCounts[licenseKey]) {
      licenseCounts[licenseKey] = { count: 0, color: licenseColor };
    }
    if (!statusCounts[statusKey]) {
      statusCounts[statusKey] = { count: 0, color: statusColor };
    }

    licenseCounts[licenseKey].count += 1;
    statusCounts[statusKey].count += 1;
  });

  const sortedLicenses = Object.entries(licenseCounts).sort(([, amountA], [, amountB]) => amountB.count - amountA.count);

  const sortedStatuses = Object.entries(statusCounts).sort(([, amountA], [, amountB]) => amountB.count - amountA.count);
  const boatModelCoverage = totalSystems ? Math.round((systemsWithBoatModel / totalSystems) * 100) : 0;
  const hullCoverage = totalSystems ? Math.round((systemsWithHullNumber / totalSystems) * 100) : 0;
  const activeCoverage = totalSystems ? Math.round((activeSystems / totalSystems) * 100) : 0;

  return (
    <div className='border-x border-b border-gray-10 bg-white px-3 py-2'>
      <div className='flex flex-wrap items-center justify-between gap-2 mb-2'>
        <span className='prose-labelStandard text-gray-60'>Insights</span>
      </div>

      <div className='grid grid-cols-2 xl:grid-cols-4 gap-2'>
        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-paragraphSmaller text-gray-60'>Systems</div>
          <div className='prose-heading4 text-gray-100'>{totalSystems}</div>
        </div>
        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-paragraphSmaller text-gray-60'>Active</div>
          <div className='prose-heading4 text-gray-100'>
            {activeSystems} ({activeCoverage}%)
          </div>
        </div>
        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-paragraphSmaller text-gray-60'>Boat model</div>
          <div className='prose-heading4 text-gray-100'>{boatModelCoverage}%</div>
        </div>
        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-paragraphSmaller text-gray-60'>Hull number</div>
          <div className='prose-heading4 text-gray-100'>{hullCoverage}%</div>
        </div>
      </div>

      <div className='grid lg:grid-cols-2 gap-2 mt-2'>
        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-labelTableColumn text-gray-60 mb-1'>License</div>
          {sortedLicenses.length > 0 ? (
            <div className='flex flex-wrap items-start gap-1 max-h-52 overflow-y-auto pr-1'>
              {sortedLicenses.map(([license, summary]) => (
                <div key={license}>
                  <StatusPill color={summary.color as any} text={`${license}: ${summary.count}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className='prose-paragraphSmaller italic text-gray-60'>No systems found for this filter.</div>
          )}
        </div>

        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-labelTableColumn text-gray-60 mb-1'>Status</div>
          {sortedStatuses.length > 0 ? (
            <div className='flex flex-wrap items-start gap-1 max-h-52 overflow-y-auto pr-1'>
              {sortedStatuses.map(([status, summary]) => (
                <div key={status}>
                  <StatusPill color={summary.color as any} text={`${status}: ${summary.count}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className='prose-paragraphSmaller italic text-gray-60'>No systems found for this filter.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemsMiniDashboard;
